<template>
  <div class="flex min-h-full flex-col bg-white dark:bg-slate-900">
    <!-- Filter bar -->
    <div class="border-b border-gray-200 dark:border-slate-700 px-4 py-4">
      <div class="flex items-center justify-between gap-3">
        <!-- All / Unread pills -->
        <div class="flex items-center gap-2">
          <button
            v-for="f in FILTERS"
            :key="f.id"
            class="shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors"
            :class="activeFilter === f.id ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-200'"
            @click="activeFilter = f.id"
          >
            {{ f.label }}
            <span
              v-if="f.id === 'unread' && notificationStore.unreadCount > 0"
              :class="[ 'ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold', activeFilter === 'unread' ? 'bg-white dark:bg-slate-900/30 text-white' : 'bg-primary/10 text-primary', ]"
            >{{ notificationStore.unreadCount }}</span>
          </button>
        </div>

        <!-- Mark all as read -->
        <button
          v-if="notificationStore.hasUnread"
          class="shrink-0 text-xs font-medium text-primary transition-colors hover:opacity-75"
          @click="markAllAsRead"
        >
          Mark all as read
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="notificationStore.isLoading" class="flex flex-1 items-center justify-center py-12">
      <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
    </div>

    <!-- Error state -->
    <div
      v-else-if="notificationStore.error"
      class="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center"
    >
      <div class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-400">
        <svg class="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 class="mb-1 text-lg font-medium text-gray-900 dark:text-slate-100">Unable to load notifications</h3>
      <p class="text-sm text-gray-500 dark:text-slate-400">{{ notificationStore.error }}</p>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="groupedNotifications.length === 0"
      class="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center"
    >
      <div class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800 text-gray-400">
        <svg class="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      </div>
      <h3 class="mb-1 text-lg font-medium text-gray-900 dark:text-slate-100">
        {{ activeFilter === 'unread' ? 'All caught up' : 'No notifications yet' }}
      </h3>
      <p class="max-w-sm text-sm text-gray-500 dark:text-slate-400">
        {{ activeFilter === 'unread' ? 'You have no unread notifications.' : 'Notifications will appear here when available.' }}
      </p>
    </div>

    <!-- Notification list, grouped by date -->
    <div v-else class="flex-1 px-4 py-2">
      <div v-for="group in groupedNotifications" :key="group.label" class="mb-4">
        <h3 class="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-slate-400">{{ group.label }}</h3>
        <NotificationCard
          v-for="item in group.items"
          :key="item.id"
          :item="item"
          :expanded="expandedId === item.id"
          @click="onCardClick"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import NotificationCard from "~/components/notifications/NotificationCard.vue";
import { useNavbarInfo } from "~/stores/useNabvarInfo";
import { useNotificationStore } from "~/stores/notificationStore";
import type { NotificationItem } from "~/types/notifications";

const navbar = useNavbarInfo();
navbar.changeInfo("Notifications", []);
useHead({ title: "Notifications" });

const userStore = useUserStore();
const notificationStore = useNotificationStore();

const expandedId = ref<string | null>(null);
const activeFilter = ref<"all" | "unread">("all");

const FILTERS = [
  { id: "all" as const, label: "All" },
  { id: "unread" as const, label: "Unread" },
];

const userId = computed(() => userStore.userDetails?.userId ?? "");

watch(userId, (id) => { if (id) notificationStore.startWatching(id); }, { immediate: true });
onUnmounted(() => notificationStore.stopWatching());

/** Notifications filtered by the active tab. */
const filteredNotifications = computed(() =>
  activeFilter.value === "unread"
    ? notificationStore.notifications.filter((n) => !n.read)
    : notificationStore.notifications,
);

/** Groups notifications under Today / Yesterday / date labels. */
const groupedNotifications = computed(() => {
  const groups: { label: string; items: NotificationItem[] }[] = [];

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(todayStart.getDate() - 1);

  for (const item of filteredNotifications.value) {
    let label = "Older";
    if (item.createdAt) {
      const d = new Date(item.createdAt);
      if (!isNaN(d.getTime())) {
        const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        if (dayStart.getTime() === todayStart.getTime()) {
          label = "Today";
        } else if (dayStart.getTime() === yesterdayStart.getTime()) {
          label = "Yesterday";
        } else {
          label = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        }
      }
    }

    const existing = groups.find((g) => g.label === label);
    if (existing) existing.items.push(item);
    else groups.push({ label, items: [item] });
  }

  return groups;
});

function onCardClick(item: NotificationItem) {
  expandedId.value = expandedId.value === item.id ? null : item.id;
  if (!item.read && userId.value) {
    notificationStore.markAsRead(userId.value, item.id);
  }
}

async function markAllAsRead() {
  const uid = userId.value;
  if (!uid) return;
  await Promise.all(
    notificationStore.notifications
      .filter((n) => !n.read)
      .map((n) => notificationStore.markAsRead(uid, n.id)),
  );
}
</script>
